import {CrewEquipmentDocuments} from '../components/CrewEquipmentDocuments';
import {CrewPhaseAssignments} from '../phases/CrewPhaseAssignments';
import {phaseStaffingEnabled, legacyStaffingDelivery} from '../phases/phaseApi';
import {nzLocal} from '../phases/model';
import { PrivateImage, PrivateVideo } from '../components/PrivateAsset';
import { uploadCrewMedia } from '../crewMedia';
import { CrewPickupPanel, PickupState } from '../components/CrewPickupPanel';
import { CrewRunSheetPriority } from '../components/CrewRunSheetPriority';
import { CrewEventCardDetails } from '../components/CrewEventCardDetails';
import { MissionControlCarousel } from '../components/MissionControlCarousel';
import { CrewEquipmentWorkspace } from '../components/CrewEquipmentWorkspace';
import { CrewEventBriefing } from '../components/CrewEventBriefing';
import { EquipmentTaskPanel, managedEquipmentLine } from '../components/EquipmentTaskPanel';
import { useEquipmentControls } from '../equipmentRuntime';
import React, { useState, useEffect, useRef } from 'react';
import { loadCrewIdentity, saveCrewProfile, crewOperation } from '../crewAccess';
import { normalizeStaffRole } from '../staffRoles';
import { crewRole, getCrewChecklist } from '../crewChecklist';
import { User, Lock, Calendar, Clock, CheckSquare, Phone, ChevronLeft, ChevronDown, ChevronUp, CheckCircle, LogOut, Mail, Image as ImageIcon, Briefcase, Users, Upload, AlertCircle, MessageSquare, Check, X, History } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { GlassCard } from '../components/ui/GlassCard';
import { supabase } from '../supabaseClient';
import { CrewReferralPanel } from '../components/CrewReferralPanel';
import './ContractorPortal.mobile.css';
import { getCrewAssignmentDisplay } from '../crewPortalDisplay';

// 🛡️ --- NEW OTP LOGIN COMPONENT --- 🛡️
export const StaffLogin: React.FC<{ onLoginSuccess: (user: any) => void }> = ({ onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [step, setStep] = useState<'request' | 'verify'>('request');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
  
    const handleRequestOtp = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      setMessage('');
  
      const { error: authError } = await supabase.auth.signInWithOtp({
        email: email.trim(),
        options: { shouldCreateUser: true }, 
      });
  
      if (authError) {
        setError(authError.message);
      } else {
        setStep('verify');
        setMessage('Code sent! Check your email.');
      }
      setLoading(false);
    };
  
    const handleVerifyOtp = async (e: React.FormEvent) => {
      e.preventDefault();
      setLoading(true);
      setError('');
      setMessage('');
  
      const { data, error } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token: otp,
        type: 'email',
      });
  
      if (error) {
        setError("Invalid or expired code. Please try again.");
        setLoading(false);
      } else if (data.user) {
        setMessage('Success! Logging you in...');
        
        try {
          const staffProfile = await loadCrewIdentity();
          onLoginSuccess(staffProfile);
        } catch {
          setMessage('');
          setError('Staff access unavailable. Please contact Admin.');
        } finally {
          setLoading(false);
        }
      }
    };
  
    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6">
          <div className="w-20 h-20 bg-gradient-to-br from-brand-neonBlue to-brand-neonPink rounded-[24px] flex items-center justify-center mb-8 shadow-[0_0_40px_rgba(236,72,153,0.3)]">
            <Lock size={36} className="text-white" />
          </div>
          <h1 className="type-page-title mb-2 text-white">Crew Portal</h1>
          <p className="text-slate-400 mb-10 text-center font-medium max-w-sm">
            {step === 'request' 
              ? "Enter your official dispatch email to receive a secure login code." 
              : "Enter the full code we just sent to your email."}
          </p>
          
          <div className="w-full max-w-sm space-y-4 bg-slate-900/50 p-6 rounded-[32px] border border-white/5">
            {error && <div className="bg-red-500/10 text-red-500 p-3 rounded-xl mb-4 text-sm text-center border border-red-500/20 font-bold">{error}</div>}
            {message && <div className="bg-green-500/10 text-green-400 p-3 rounded-xl mb-4 text-sm text-center border border-green-500/20 font-bold">{message}</div>}
  
            {step === 'request' ? (
              <form onSubmit={handleRequestOtp} className="space-y-4">
                <div className="bg-slate-950 border border-slate-800 rounded-2xl flex items-center px-5 py-4 focus-within:border-brand-neonPink transition-colors">
                  <Mail size={20} className="text-slate-500 mr-4 shrink-0" />
                  <input 
                      type="email" 
                      placeholder="crew@example.com" 
                      className="bg-transparent text-white w-full outline-none font-medium" 
                      value={email} 
                      onChange={e => setEmail(e.target.value)} 
                      required 
                  />
                </div>
                <Button disabled={loading} className="w-full py-5 text-lg rounded-2xl bg-brand-neonPink text-white shadow-neon-pink font-bold">
                    {loading ? 'Sending Code...' : 'Send Login Code'}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="bg-slate-950 border border-slate-800 rounded-2xl flex items-center px-5 py-4 focus-within:border-brand-neonBlue transition-colors">
                  <Lock size={20} className="text-brand-neonBlue mr-4 shrink-0" />
                  <input 
                      type="text" 
                      placeholder="Login code"
                      aria-label="Email login code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="[0-9]{6,10}"
                      minLength={6}
                      className="bg-transparent text-white w-full min-w-0 outline-none font-mono text-xl tracking-widest text-center"
                      value={otp} 
                      onChange={e => setOtp(e.target.value)} 
                      maxLength={10}
                      required 
                  />
                </div>
                <Button disabled={loading} className="w-full py-5 text-lg rounded-2xl bg-brand-neonBlue text-slate-950 shadow-neon font-bold">
                    {loading ? 'Verifying...' : 'Verify & Access Roster'}
                </Button>
                <button
                    type="button"
                    onClick={() => { setStep('request'); setOtp(''); setError(''); }}
                    className="w-full text-slate-500 text-xs hover:text-white mt-4 font-bold tracking-widest uppercase transition-colors"
                >
                  Wrong email? Go back
                </button>
              </form>
            )}
          </div>
        </div>
    );
};


// 🚀 --- MAIN PORTAL COMPONENT --- 🚀
export const ContractorPortal = () => {
  const equipmentControls=useEquipmentControls();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  const [view, setView] = useState<'LIST' | 'DETAIL'>('LIST');
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'OVERVIEW' | 'TRAVEL' | 'TASKS' | 'GEAR' | 'CREW'>('OVERVIEW');
  
  // Data State
  const [availableShifts, setAvailableShifts] = useState<any[]>([]);
  const [phaseJobs,setPhaseJobs]=useState<any[]>([]);
  const [phaseHolds,setPhaseHolds]=useState<any[]>([]);
  const staffingRequest=useRef(0);
  const [staffingLoadError,setStaffingLoadError]=useState('');
  const [phaseHistory,setPhaseHistory]=useState<any[]>([]);
  const [myJobs, setMyJobs] = useState<any[]>([]);
  const [pastJobs, setPastJobs] = useState<any[]>([]);
  const [showPastJobs, setShowPastJobs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processingJobId, setProcessingJobId] = useState<string | null>(null); 
  const [jobBlueprint, setJobBlueprint] = useState<any>(null);
  const [pickup, setPickup] = useState<PickupState | null>(null);
  const [isFetchingBlueprint, setIsFetchingBlueprint] = useState(false);
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [isMissionUploading, setIsMissionUploading] = useState(false);
  const missionSaving = useRef(false);
  const eventOpenRequest = useRef(0); // ⚡ NEW: Mission Photo Upload State
  
  // ⚡ SMART CALENDAR STATE
  
  // ⚡ SMART CALENDAR STATE
  const [listTab, setListTab] = useState<'JOBS' | 'TASKS' | 'REFER' | 'AVAILABILITY' | 'PROFILE'>('JOBS');
  const [myTasks, setMyTasks] = useState<any[]>([]); // ⚡ Holds personal prep tasks
  const [expandedTasks, setExpandedTasks] = useState<string[]>([]); // ⚡ Tracks which tasks are tapped open
  const [profileForm, setProfileForm] = useState<any>({});
  
  // Load current user data into the form when they view their profile
  useEffect(() => {
      if (currentUser) setProfileForm(currentUser);
  }, [currentUser]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availabilities, setAvailabilities] = useState<Record<string, any>>({});
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [availForm, setAvailForm] = useState({ status: 'AVAILABLE', startTime: '', endTime: '' });

  // ⚡ FETCH AVAILABILITY
  const fetchAvailability = async (staffId: string) => {
    const { data, error } = await supabase.from('staff_availability').select('*').eq('staff_id', staffId);
    if (!error && data) {
        const availMap: Record<string, any> = {};
        data.forEach(item => { 
            const cleanDate = item.date.split('T')[0]; 
            availMap[cleanDate] = item; 
        });
        setAvailabilities(availMap);
    } else if (error) {
        console.error("Error fetching schedule:", error);
    }
  };

  // ⚡ SUPERCHARGED FORCE DOWNLOAD (Bypasses Browser Security Blocks)
  const handleForceDownload = (e: React.MouseEvent, fileUrl: string, fallbackName: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // The Supabase Cheat Code: Tells the server to force it as a file attachment
    const downloadUrl = fileUrl.includes('?') ? `${fileUrl}&download=` : `${fileUrl}?download=`;
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', fallbackName || 'quikpix-media');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ⚡ FETCH PERSONAL TASKS (UPGRADED SAFELY WITH SMART SHIFT MATCHING)
  const fetchMyTasks = async (staffId: string) => {
    try {
        const { data: tasksData, error } = await supabase
            .from('tasks')
            .select('*, lead:event_leads(*)') 
            .eq('assignee_id', staffId)
            .order('due_date', { ascending: true });
            
        if (error) throw error;
        
        if (tasksData) {
            // ⚡ FIX: Used classic Array deduplication to bypass TypeScript 'Set' iteration error
            const rawLeadIds = tasksData.map(t => t.lead_id).filter(Boolean);
            const leadIds = rawLeadIds.filter((item, index) => rawLeadIds.indexOf(item) === index);
            
            const rawShiftIds = tasksData.map(t => t.shift_id || t.shiftId || t.event_shift_id).filter(Boolean);
            const shiftIds = rawShiftIds.filter((item, index) => rawShiftIds.indexOf(item) === index);
            
            const [quotesRes, leadShiftsRes, directShiftsRes] = await Promise.all([
                leadIds.length > 0 ? supabase.rpc('crew_quote_blueprints') : Promise.resolve({ data: [] }),
                leadIds.length > 0 ? supabase.rpc('crew_task_shifts') : Promise.resolve({ data: [] }),
                shiftIds.length > 0 ? supabase.from('event_shifts').select('id, lead_id, shift_title, event_date').in('id', shiftIds) : Promise.resolve({ data: [] })
            ]);
            
            const quotesData = quotesRes.data || [];
            const allFetchedShifts = [...(leadShiftsRes.data || []), ...(directShiftsRes.data || [])];
            
            // ⚡ FIX: Also bypassed 'Map' iteration just to be extra safe for your TS config!
            const uniqueShifts = allFetchedShifts.filter((shift, index, self) => 
                index === self.findIndex((t) => t.id === shift.id)
            );
            
            const enrichedTasks = tasksData.map(task => {
                const relatedQuote = quotesData.find((q:any) => q.lead_id === task.lead_id && q.operational);
                
                // 1. Try to find by direct ID link first
                let relatedShift = uniqueShifts.find(s => s.id === task.shift_id || s.id === task.shiftId || s.id === task.event_shift_id);
                
                // 2. SMART FALLBACK: If no direct link, find the shift for this lead that matches the task's due date!
                if (!relatedShift && task.lead_id) {
                    const leadShifts = uniqueShifts.filter(s => s.lead_id === task.lead_id);
                    if (leadShifts.length > 0) {
                        // Sort chronologically
                        leadShifts.sort((a, b) => new Date(a.event_date).getTime() - new Date(b.event_date).getTime());
                        
                        // Look for an exact date match first
                        const exactMatch = leadShifts.find(s => s.event_date && task.due_date && s.event_date.split('T')[0] === task.due_date.split('T')[0]);
                        
                        if (exactMatch) {
                            relatedShift = exactMatch;
                        } else {
                            // Fallback to the first shift that happens ON or AFTER the task due date
                            relatedShift = leadShifts.find(s => new Date(s.event_date) >= new Date(task.due_date)) || leadShifts[leadShifts.length - 1];
                        }
                    }
                }
                
                return {
                    ...task,
                    fieldCrewNotes: relatedQuote?.crewNotes || null,
                    shift_data: relatedShift || null
                };
            });
            
            setMyTasks(enrichedTasks);
        }
    } catch (err: any) {
        console.error("System Error Fetching Tasks:", err);
    }
  };

  // ⚡ SAVE AVAILABILITY
  const handleSaveAvailability = async () => {
    if (!currentUser || !selectedDate) return;
    
    const payload: any = {
        staff_id: currentUser.id,
        date: selectedDate,
        status: availForm.status,
        start_time: availForm.status === 'PARTIAL' ? availForm.startTime || null : null,
        end_time: availForm.status === 'PARTIAL' ? availForm.endTime || null : null
    };

    try {
        const savedRecord = await crewOperation('crew_availability', {
          p_date: selectedDate, p_status: payload.status, p_start: payload.start_time, p_end: payload.end_time,
        });
        setAvailabilities(prev => ({ ...prev, [selectedDate]: savedRecord }));
        setSelectedDate(null);
    } catch (err: any) {
        alert("Failed to save schedule. Please check your connection. Error: " + err.message);
    }
  };

  useEffect(() => {
    const checkSession = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session && session.user) {
            const staffProfile = await loadCrewIdentity().catch(() => null);

                if (staffProfile) {
                  setCurrentUser(staffProfile);
                  setIsAuthenticated(true);
                  fetchJobs(staffProfile.id);
                  fetchAvailability(staffProfile.id); 
                  fetchMyTasks(staffProfile.id); // ⚡ Fetch tasks instantly
              }
        }
    };
    checkSession();
  }, []);

  useEffect(()=>{
    if(!phaseStaffingEnabled()||!isAuthenticated||!currentUser?.id)return;
    const refresh=()=>{if(document.visibilityState==='visible')fetchJobs(currentUser.id);};
    const timer=window.setInterval(refresh,15000);
    window.addEventListener('focus',refresh);document.addEventListener('visibilitychange',refresh);
    return()=>{window.clearInterval(timer);window.removeEventListener('focus',refresh);document.removeEventListener('visibilitychange',refresh);};
  },[isAuthenticated,currentUser?.id]);
  const formatTime = (timeStr?: string) => {
    if (!timeStr) return '--:--';
    try {
        const [hours, minutes] = timeStr.split(':');
        const h = parseInt(hours, 10);
        const m = parseInt(minutes, 10);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const hr12 = h % 12 || 12;
        return `${hr12}:${m.toString().padStart(2, '0')} ${ampm}`;
    } catch (e) {
        return timeStr;
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAuthenticated(false);
    staffingRequest.current++;setPhaseJobs([]);setPhaseHistory([]);setPhaseHolds([]);setPastJobs([]);setSelectedJob(null);setStaffingLoadError('');
    setCurrentUser(null);
    setAvailableShifts([]);
    setMyJobs([]);
    setView('LIST');
  };

  const fetchJobs = async (staffId: string) => {
      const request=++staffingRequest.current;
      setIsLoading(true);
      try {
          const response = await supabase.rpc(phaseStaffingEnabled()?'crew_staffing_workspace':'crew_jobs');
          if(request!==staffingRequest.current)return;
          if(phaseStaffingEnabled()&&!response.error&&(!Array.isArray(response.data?.legacy)||!Array.isArray(response.data?.phases)||!Array.isArray(response.data?.history)))throw Error('Invalid staffing source response');
          const shifts=phaseStaffingEnabled()?response.data?.legacy:response.data;const error=response.error;
          if(phaseStaffingEnabled()){setPhaseJobs(response.data?.phases||[]);setPhaseHistory(response.data?.history||[]);setPhaseHolds(response.data?.holds||[]);}

          if (error) throw error;
          setStaffingLoadError('');

          const available: any[] = [];
          const upcoming: any[] = [];
          const past: any[] = []; // ⚡ NEW: The History Bucket

          const today = new Date();
          today.setHours(0, 0, 0, 0);

          (shifts || []).forEach((shift: any) => {
              const roster = shift.staff_roster;
              if (!roster || !roster.crew) return;

              const myRecord = roster.crew.find((c: any) => c.id === staffId);
              if (!myRecord) return;
              const assignmentDisplay = getCrewAssignmentDisplay(myRecord);

              const master = shift.event_leads || {};

              const jobCard = {
                id: shift.id,
                lead_id: shift.lead_id, 
                title: shift.shift_title || master.project_ref || 'Unnamed Shift',
                clientName: `${master.first_name || ''} ${master.last_name || ''}`.trim() || 'Private Client',
                companyName: master.company_name || 'Private Account',
                phone: master.phone || '',
                eventDate: shift.event_date || 'TBD',
                venue: shift.venue_location || master.venue_location || 'TBD',
                duration: null,
                setupTime: shift.setup_time || null,
                startTime: shift.start_time || null,
                endTime: shift.end_time || null,
                packDownTime: shift.pack_down_time || null,
                staff_roster: shift.staff_roster,
                equipmentRevision: shift.equipment_revision || 0,
                equipmentRequired: (shift.equipment_list || []).length > 0,
                travel_arrangements: shift.travel_arrangements || '', 
                shift_notes: shift.shift_notes || '',
                operationalNotes: shift.operationalNotes || [],
                media_urls: shift.media_urls || [],                
                myStatus: myRecord.status,
                // The Run Sheet must display this contractor's rostered call time only.
                // Setup and event-start fallbacks would mislead attendants and multi-day crew.
                myCallTime: myRecord.call_time || null,
                myTimeLabel: assignmentDisplay.timeLabel,
                equipmentArrivalLabel: assignmentDisplay.equipmentArrivalLabel,
                equipmentPickupTime: assignmentDisplay.equipmentPickupTime,
            };

              if (shift.event_date) {
                  // 1. Bulletproof timezone fix (strips times, just compares exact YYYY-MM-DD strings)
                  const cleanShiftDate = shift.event_date.split('T')[0];
                  const todayLocal = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                  
                  const leadStatus = String(master.status || '').toUpperCase();
                  
                  // 2. We removed COMPLETED and POST_EVENT here so multi-day jobs don't disappear if the master lead changes!
                  const isDead = ['LOST', 'WITHDRAWN', 'ARCHIVED'].includes(leadStatus);
                  const isPast = cleanShiftDate < todayLocal;

                  // ⚡ IF PAST OR DEAD: Push to the History bucket instead!
                  if (isPast || isDead) {
                      // Only show them in history if they actually worked the shift!
                      if (myRecord.status === 'ACCEPTED' || myRecord.status === 'CONFIRMED' || myRecord.status === 'COMPLETED') {
                          past.push(jobCard);
                      }
                      return; 
                  }
              }

              if (myRecord.status === 'PENDING') {
                  available.push(jobCard);
              } else if (myRecord.status === 'ACCEPTED' || myRecord.status === 'CONFIRMED') {
                  upcoming.push(jobCard);
              }
          });

          setAvailableShifts(available);
          setMyJobs(upcoming);
          if(phaseStaffingEnabled())setSelectedJob((current:any)=>current&&!shifts.some((shift:any)=>shift.id===current.id)?null:current);
          setPastJobs(past.reverse()); // ⚡ Reverse so the most recent past jobs are at the top
        } catch (err: any) {
          if(request!==staffingRequest.current)return;
          if(phaseStaffingEnabled()){setPhaseJobs([]);setPhaseHistory([]);setPhaseHolds([]);setAvailableShifts([]);setMyJobs([]);setPastJobs([]);setSelectedJob(null);setView('LIST');setStaffingLoadError('Staffing source could not be verified. Refresh before responding to an assignment.');}
          else alert('Failed to fetch shifts: ' + err.message);
      } finally {
          setIsLoading(false);
      }
  };

  const handleAcceptShift = async (e: React.MouseEvent, job: any) => {
    e.stopPropagation();
    setProcessingJobId(job.id); 

    try {
        await crewOperation('crew_shift_action', { p_shift: job.id, p_action: 'response', p_value: 'ACCEPTED' });

        await legacyStaffingDelivery(job.id,async()=>{const response=await fetch('https://hook.us2.make.com/52e886sl5b5di7vy8giefqzbw79n1htm', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                action: 'SHIFT_ACCEPTED',
                staffName: currentUser.full_name || currentUser.name,
                jobId: job.id,
                eventName: job.title,
                eventDate: job.eventDate
            })
        });if(!response.ok)throw Error('Crew briefing request failed');}).catch(err=>console.warn('Shift response saved; briefing delivery requires review.'));

        alert("Shift Accepted! Waiting for Admin Confirmation.");
        await fetchJobs(currentUser.id);
    } catch (err: any) {
        alert('Failed to accept shift: ' + err.message);
    } finally {
        setProcessingJobId(null);
    }
  };

  const handleDeclineShift = async (e: React.MouseEvent, job: any) => {
    e.stopPropagation();
    if (!window.confirm("Decline this shift? This will automatically mark you as UNAVAILABLE on your calendar for this date.")) return;

    setProcessingJobId(job.id); 

    try {
        await crewOperation('crew_shift_action', { p_shift: job.id, p_action: 'response', p_value: 'DECLINED' });
        await fetchAvailability(currentUser.id);
        alert("Shift declined. Your calendar has been updated.");
        await fetchJobs(currentUser.id);
    } catch (err: any) {
        alert('Failed to decline shift: ' + err.message);
    } finally {
        setProcessingJobId(null);
    }
  };

  // ⚡ NEW: PROGRESS SAVING ENGINE
  const handleCompleteMissionStep = async (stepId: string) => {
      if (missionSaving.current) return;
      missionSaving.current = true;
      setIsMissionUploading(true);
      const shiftId = selectedJob.id;
      try {
          const saved = await crewOperation('crew_shift_action', { p_shift: shiftId, p_action: 'mission', p_value: stepId });
          if (!saved || saved.id !== shiftId || !saved.staff_roster?.crew) throw Error('Saved progress could not be verified. Reopen the event.');
          const reconcile = (job: any) => job?.id === shiftId ? { ...job, staff_roster: saved.staff_roster } : job;
          setSelectedJob(reconcile);
          setMyJobs(previous => previous.map(reconcile));
          setPastJobs(previous => previous.map(reconcile));
          setAvailableShifts(previous => previous.map(reconcile));
      } catch (err: any) {
          alert("Failed to save progress: " + err.message);
      } finally {
          missionSaving.current = false;
          setIsMissionUploading(false);
      }
  };

  const handleSelectJob = async (job: any) => {
    const request = ++eventOpenRequest.current;
    setIsLoading(true);
    try {
      const response = await supabase.rpc(phaseStaffingEnabled() ? 'crew_staffing_workspace' : 'crew_jobs');
      if (request !== eventOpenRequest.current) return;
      if (response.error) throw response.error;
      const records = phaseStaffingEnabled() ? response.data?.legacy : response.data;
      if (!Array.isArray(records)) throw Error('Current staffing could not be verified');
      const fresh = records.find((record: any) => record.id === job.id);
      const member = fresh?.staff_roster?.crew?.find((record: any) => record.id === currentUser.id);
      if (!fresh || !member) throw Error('This assignment is no longer available. Refresh your roster.');
      job = { ...job, staff_roster: fresh.staff_roster, myStatus: member.status,
        media_urls: fresh.media_urls || [], equipmentRevision: fresh.equipment_revision || 0,
        equipmentRequired: (fresh.equipment_list || []).length > 0,
        eventDate: fresh.event_date, setupTime: fresh.setup_time, startTime: fresh.start_time,
        endTime: fresh.end_time, packDownTime: fresh.pack_down_time, venue: fresh.venue_location,
        shift_notes: fresh.shift_notes || '', travel_arrangements: fresh.travel_arrangements || '',
        myCallTime: member.call_time || null };
    } catch (error: any) {
      if (request !== eventOpenRequest.current) return;
      setSelectedJob(null); setView('LIST'); alert('Could not open current event: ' + error.message); return;
    } finally { if (request === eventOpenRequest.current) setIsLoading(false); }
    setSelectedJob(job);
    setView('DETAIL');
    setActiveTab('OVERVIEW'); 
    setJobBlueprint(null); 
    setPickup(null);

    if (job.myStatus === 'CONFIRMED') {
        setIsFetchingBlueprint(true);
        try {
            const {data: assignedQuotes,error}=await supabase.rpc('crew_quote_blueprints');
            const quoteData=(assignedQuotes || []).find((q:any)=>q.lead_id===job.lead_id);

            if (!error && quoteData) {
              setJobBlueprint({
                  quoteId: quoteData.id,
                  data: {...(quoteData.operational?.data || {}),crewNotes:quoteData.crewNotes||''},
                  structure: quoteData.operational?.structure || [],
                  operationalNotes: quoteData.operationalNotes || [],
                  equipmentDocuments: quoteData.equipmentDocuments || []
              });
          }
        } catch (err) {
            console.error("Blueprint fetch error:", err);
        } finally {
            setIsFetchingBlueprint(false);
        }
    }
  };

  if (!isAuthenticated) {
    return (
        <StaffLogin 
        onLoginSuccess={(userProfile) => {
          setCurrentUser(userProfile);
          setIsAuthenticated(true);
          fetchJobs(userProfile.id);
          fetchAvailability(userProfile.id); 
          fetchMyTasks(userProfile.id); // ⚡ Fetch tasks here
      }}
        />
    );
  }

  // --- JOB LIST VIEW ---
  if (view === 'LIST' || !selectedJob) {
    
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    const firstDayOfMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay();
    const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

    return (
      <div className="min-h-screen bg-slate-950 pb-24 relative">
        <div className="sticky top-0 bg-slate-950/80 backdrop-blur-2xl border-b border-white/10 p-6 z-20 flex flex-col gap-4 shadow-2xl">
          <div className="flex justify-between items-center">
              <div>
                  <h1 className="type-page-title text-white">{currentUser?.full_name?.split(' ')[0]}'s Roster</h1>
                  <p className="type-label text-brand-neonPink mt-1">{currentUser?.role || 'Crew'}</p>
              </div>
              <button onClick={handleLogout} className="p-3 bg-slate-900 rounded-2xl text-slate-400 hover:text-white border border-white/5 transition-colors">
                <LogOut size={20} />
              </button>
          </div>
          
          <div className="crew-roster-navigation flex bg-slate-900 p-1.5 rounded-2xl border border-white/5 overflow-x-auto custom-scrollbar gap-1">
              <button 
                  onClick={() => setListTab('JOBS')} 
                  className={`flex-1 py-2.5 px-4 whitespace-nowrap type-button text-xs rounded-xl transition-all ${listTab === 'JOBS' ? 'bg-white text-slate-950 shadow-md' : 'text-slate-500 hover:text-white'}`}
              >
                  Gig Roster
              </button>
              
              <button 
                  onClick={() => setListTab('TASKS')} 
                  className={`flex-1 py-2.5 px-4 whitespace-nowrap type-button text-xs rounded-xl transition-all relative ${listTab === 'TASKS' ? 'bg-yellow-500 text-slate-950 shadow-[0_0_15px_rgba(234,179,8,0.4)]' : 'text-slate-500 hover:text-white'}`}
              >
                  {/* ⚡ DYNAMIC TAB NAME */}
                  {currentUser?.role === 'Equipment Manager' ? 'My Prep Tasks' : 'My Tasks'}
                  
                  {myTasks.filter(t => t.status !== 'COMPLETED').length > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-[10px] shadow-lg animate-pulse border-2 border-slate-900">
                          {myTasks.filter(t => t.status !== 'COMPLETED').length}
                      </span>
                  )}
              </button>

              <button
                  onClick={() => setListTab('REFER')}
                  className={`flex-1 py-2.5 px-4 whitespace-nowrap type-button text-xs rounded-xl transition-all ${listTab === 'REFER' ? 'bg-brand-neonBlue text-slate-950 shadow-neon-blue-sm' : 'text-slate-500 hover:text-white'}`}
              >
                  Refer an Enquiry
              </button>

              <button
                  onClick={() => setListTab('AVAILABILITY')} 
                  className={`flex-1 py-2.5 px-4 whitespace-nowrap type-button text-xs rounded-xl transition-all ${listTab === 'AVAILABILITY' ? 'bg-brand-neonBlue text-slate-950 shadow-neon-blue-sm' : 'text-slate-500 hover:text-white'}`}
              >
                  My Schedule
              </button>
              <button 
                  onClick={() => setListTab('PROFILE')} 
                  className={`flex-1 py-2.5 px-4 whitespace-nowrap type-button text-xs rounded-xl transition-all ${listTab === 'PROFILE' ? 'bg-brand-neonPink text-white shadow-neon-pink' : 'text-slate-500 hover:text-white'}`}
              >
                  My Profile
              </button>
          </div>
        </div>
        
        <div className="p-6 max-w-2xl mx-auto space-y-10">
          
          {listTab==='JOBS'&&<>{staffingLoadError&&<p role="alert">{staffingLoadError}</p>}{phaseHolds.map(h=><p role="status" key={h.leadId}>Staffing under review: {h.event}. Do not use previous phase instructions. Contact QuikPix to confirm your responsibilities.</p>)}<CrewPhaseAssignments jobs={phaseJobs} history={phaseHistory} onReload={()=>fetchJobs(currentUser.id)}/></>}
          {/* ⚡ RENDER TASKS, PROFILE, CALENDAR, OR JOBS */}
          {listTab === 'TASKS' ? (
              <div className="animate-fade-in-up space-y-6">
                  <div className="bg-slate-900/60 border border-white/10 rounded-[32px] p-6 md:p-8 shadow-xl">
                  <div className="mb-6 border-b border-white/5 pb-4">
                          {/* ⚡ DYNAMIC HEADER TITLES */}
                          <h3 className="type-heading text-2xl text-white md:text-3xl">
                              {currentUser?.role === 'Equipment Manager' ? 'Equipment & Prep' : 'Action Items'}
                          </h3>
                          <p className="type-label text-yellow-500 mt-1">
                              {currentUser?.role === 'Equipment Manager' ? 'Warehouse Task Checklist' : 'Pending Tasks & Requests'}
                          </p>
                      </div>
                      
                      <div className="space-y-6">
                          {myTasks.length === 0 ? (
                              <div className="text-center py-12 border-2 border-dashed border-white/10 rounded-2xl text-slate-500 font-medium">
                                  <CheckCircle size={32} className="mx-auto mb-3 opacity-20" />
                                  All caught up! No pending tasks.
                              </div>
                          ) : (
                              <>
                                  {/* 🔴 PENDING TASKS */}
                                  {myTasks.filter(t => t.status !== 'COMPLETED').map(task => {
                                              const isExpanded = expandedTasks.includes(task.id);
                                              return (
                                              <div key={task.id} className="bg-slate-950/50 rounded-xl border border-white/5 hover:border-yellow-500/30 transition-all shadow-lg group overflow-hidden">
                                                   <div className="p-4 flex items-start gap-4 cursor-pointer" onClick={() => setExpandedTasks(prev => isExpanded ? prev.filter(id => id !== task.id) : [...prev, task.id])}>
                                                       <button 
                                                          onClick={async (e) => {
                                                              e.stopPropagation();
                                                              try {
 const saved = await crewOperation('crew_task_status', { p_task: task.id, p_status: 'COMPLETED' });
 setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: saved.status } : t));
} catch (error: any) { alert(error.message); }
                                                          }}
                                                          className="w-11 h-11 rounded-full border-2 border-slate-600 hover:border-green-500 hover:bg-green-500/20 flex items-center justify-center transition-all shrink-0 mt-0.5"
                                                          disabled={!!task.event_shift_id && !equipmentControls.logistics}
                                                          title="Mark Completed"
                                                       >
                                                           <Check size={14} className="text-transparent hover:text-green-500" />
                                                       </button>
                                                       
                                                       <div className="flex-1 min-w-0">
                                                           <p className="text-white font-bold text-sm leading-snug mb-1.5">{task.title}</p>
                                                           
                                                           {/* ⚡ UPGRADED: TASK DUE DATE + EVENT DATE CONTEXT */}
                                                           <div className="flex flex-wrap items-center gap-y-2 gap-x-3 type-label text-slate-500">
                                                               <span className={`${new Date(task.due_date) < new Date() ? 'text-red-400 animate-pulse' : 'text-yellow-500'}`}>
                                                                  Due: {new Date(task.due_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                                                               </span>
                                                               
                                                               {/* ⚡ FIX: Uses the safely attached shift data, falls back to lead date */}
                                                               {(task.shift_data?.event_date || task.lead?.event_date) && (
                                                                   <span className="text-brand-neonPink bg-brand-neonPink/10 px-2 py-0.5 rounded border border-brand-neonPink/20">
                                                                       For Event: {new Date(task.shift_data?.event_date || task.lead?.event_date).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
                                                                   </span>
                                                               )}

                                                               {/* ⚡ FIX: Prioritizes Shift Title over Master Project Name */}
                                                               <span className="truncate border-l border-white/10 pl-3 text-slate-400">
                                                                   {task.shift_data?.shift_title || task.lead?.project_ref || task.lead?.eventName || 'Admin Task'}
                                                               </span>
                                                           </div>
                                                       </div>
                                                       
                                                       <div className="shrink-0 mt-1">
                                                           {isExpanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
                                                       </div>
                                                   </div>
                                                   
                                                   {/* ⚡ UPGRADED: HANDLES EMPTY DESCRIPTIONS GRACEFULLY */}
                                                   {isExpanded && (
                                                       <div className="p-4 pt-4 border-t border-white/5 bg-black/20 mt-2 animate-fade-in">
                                                           {task.event_shift_id && task.logistics_purpose && <><EquipmentTaskPanel taskId={task.id} status={task.status} /><CrewEquipmentDocuments shiftId={task.event_shift_id}/></>}
                                                           {task.description ? (
                                                               <div className="space-y-2">
                                                                   {task.description.split('\n').map((line: string, lineIndex: number) => {
                                                                       if (task.event_shift_id && task.logistics_purpose && managedEquipmentLine(task.description, lineIndex)) return null;
                                                                       const isUnchecked = line.startsWith('• ');
                                                                       const isChecked = line.startsWith('✓ ');

                                                                       if (isUnchecked || isChecked) {
                                                                           const cleanText = line.substring(2);
                                                                           return (
                                                                               <div 
                                                                                   key={lineIndex}
                                                                                   onClick={async (e) => {
                                                                                       e.stopPropagation();
                                                                                       try {
 const saved = await crewOperation('crew_task_tick', { p_task: task.id, p_index: lineIndex, p_expected: line, p_checked: !isChecked });
 setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, description: saved.description } : t));
} catch (error: any) { alert(error.message); }
                                                                                   }}
                                                                                   className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all ${isChecked ? 'bg-brand-neonBlue/10 opacity-50' : 'hover:bg-white/5 bg-slate-900/50 border border-white/5'}`}
                                                                               >
                                                                                   <div className={`w-5 h-5 rounded flex items-center justify-center border shrink-0 transition-colors ${isChecked ? 'bg-brand-neonBlue border-brand-neonBlue text-black shadow-[0_0_10px_rgba(34,211,238,0.5)]' : 'border-slate-500 text-transparent'}`}>
                                                                                       <Check size={12} className="font-bold" />
                                                                                   </div>
                                                                                   <span className={`text-sm transition-all ${isChecked ? 'line-through text-slate-500' : 'text-slate-300'}`}>
                                                                                       {cleanText}
                                                                                   </span>
                                                                               </div>
                                                                           );
                                                                       }

                                                                       if (line.trim() === '') return null;
                                                                       return (
                                                                           <p key={lineIndex} className="text-[10px] text-slate-400 font-black uppercase tracking-widest mb-1 mt-4 first:mt-0">
                                                                               {line}
                                                                           </p>
                                                                       );
                                                                   })}
                                                                   
                                                                   {/* ⚡ NEW: FIELD CREW NOTES DISPLAY */}
                                                                   {task.fieldCrewNotes && (
                                                                       <div className="mt-6 bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 shadow-[0_0_15px_rgba(234,179,8,0.05)] animate-fade-in">
                                                                           <h4 className="type-label text-yellow-500 flex items-center gap-2 mb-2">
                                                                               <MessageSquare size={14} /> Field Crew Notes
                                                                           </h4>
                                                                           <p className="text-sm text-yellow-100/90 whitespace-pre-wrap leading-relaxed">
                                                                               "{task.fieldCrewNotes}"
                                                                           </p>
                                                                       </div>
                                                                   )}
                                                                   
                                                               </div>
                                                           ) : (
                                                               <div className="text-center py-4 opacity-50">
                                                                   <p className="type-label text-slate-400">No additional details provided</p>
                                                               </div>
                                                           )}
                                                       </div>
                                                   )}
                                              </div>
                                          )})}

                                  {/* 🟢 COMPLETED TASKS (READY FOR DISPATCH ZONE) */}
                                  {myTasks.filter(t => t.status === 'COMPLETED').length > 0 && (
                                      <div className="mt-8 pt-6 border-t border-white/10 animate-fade-in">
                                          <h4 className="type-label text-green-500 mb-4 flex items-center gap-2 px-2">
                                              <CheckSquare size={14} /> 
                                              {currentUser?.role === 'Equipment Manager' ? 'Ready For Dispatch' : 'Completed Tasks'}
                                          </h4>
                                          <div className="space-y-3">
                                              {myTasks.filter(t => t.status === 'COMPLETED').map(task => (
                                                  <div key={task.id} className="bg-green-500/10 border border-green-500/30 p-5 rounded-2xl flex flex-wrap items-center justify-between gap-3 group shadow-[0_0_20px_rgba(34,197,94,0.05)]">
                                                      <div>
                                                          <p className="text-white font-bold text-sm mb-1">{task.title}</p>
                                                          <div className="flex items-center gap-1.5 text-[10px] text-green-400 font-black uppercase tracking-widest">
                                                              <CheckCircle size={12} /> Signed Off
                                                          </div>
                                                      </div>
                                                      <button 
                                                          onClick={async () => {
                                                              try {
 const saved = await crewOperation('crew_task_status', { p_task: task.id, p_status: 'TODO' });
 setMyTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: saved.status } : t));
} catch (error: any) { alert(error.message); }
                                                          }}
                                                          disabled={!!task.event_shift_id && !equipmentControls.logistics}
                                                          className="min-h-[44px] type-label text-slate-500 hover:text-white transition-colors underline decoration-slate-500/50 underline-offset-4 px-2"
                                                      >
                                                          Undo
                                                      </button>
                                                      {task.event_shift_id && task.logistics_purpose && <><EquipmentTaskPanel taskId={task.id} status={task.status} /><CrewEquipmentDocuments shiftId={task.event_shift_id}/></>}
                                                      {task.event_shift_id && task.logistics_purpose && task.description && <p className="w-full whitespace-pre-wrap [overflow-wrap:anywhere] text-sm text-slate-300">{task.description.split('\n').filter((_: string, index: number) => !managedEquipmentLine(task.description, index)).join('\n')}</p>}
                                                  </div>
                                              ))}
                                          </div>
                                      </div>
                                  )}
                              </>
                          )}
                      </div>
                  </div>
              </div>
          ) : listTab === 'REFER' ? (
              <CrewReferralPanel rosteredEvents={[...availableShifts, ...myJobs]} />
          ) : listTab === 'PROFILE' ? (
              <div className="animate-fade-in-up space-y-6">
                  <div className="bg-slate-900/60 border border-white/10 rounded-[32px] p-6 md:p-8 shadow-xl">
                  <div className="flex items-center gap-6 mb-8 pb-6 border-b border-white/5">
                          <div className="relative group shrink-0">
                              <div className="w-20 h-20 md:w-24 md:h-24 bg-slate-800 rounded-full flex items-center justify-center text-slate-500 font-display font-bold text-3xl border-2 border-brand-neonPink/30 overflow-hidden shadow-xl">
                                  {profileForm.avatar_url ? (
                                      <PrivateImage src={profileForm.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                                  ) : (
                                      <User size={32} />
                                  )}
                              </div>
                              
                              <label htmlFor="avatar-upload" className="absolute -bottom-2 -right-2 w-8 h-8 md:w-10 md:h-10 bg-brand-neonPink hover:bg-white text-slate-950 rounded-full flex items-center justify-center cursor-pointer transition-colors shadow-neon-pink border-2 border-slate-900">
                                  <ImageIcon size={14} className="md:w-5 md:h-5" />
                              </label>
                              <input 
                                  type="file" 
                                  id="avatar-upload" 
                                  className="hidden" 
                                  accept="image/*"
                                  onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file || !currentUser) return;
                                      
                                      try {
                                          const publicUrl = await uploadCrewMedia(file, currentUser.id);
                                          
                                          const savedProfile = await saveCrewProfile({ avatar_url: publicUrl });
                                          setProfileForm({ ...profileForm, avatar_url: savedProfile.avatar_url });
                                          setCurrentUser(savedProfile);
                                          
                                          alert("Profile photo updated!");
                                      } catch (err: any) {
                                          alert("Failed to upload photo: " + err.message);
                                      }
                                  }}
                              />
                          </div>
                          
                          <div>
                              <h3 className="type-heading text-2xl text-white md:text-3xl">{currentUser?.full_name || 'My Profile'}</h3>
                              <p className="text-[10px] text-brand-neonPink uppercase tracking-widest font-black mt-1">{currentUser?.role || 'Crew Member'}</p>
                          </div>
                      </div>

                      <div className="space-y-6">
                          <div className="space-y-2">
                              <label className="type-label text-slate-500 ml-1">Full Legal Name (For Flights)</label>
                              <input className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink" value={profileForm.legal_name || profileForm.full_name || ''} onChange={e => setProfileForm({...profileForm, legal_name: e.target.value})} placeholder="Exactly as it appears on your ID" />
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                  <label className="type-label text-slate-500 ml-1">Date of Birth</label>
                                  <input type="date" className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink [color-scheme:dark]" value={profileForm.dob || ''} onChange={e => setProfileForm({...profileForm, dob: e.target.value})} />
                              </div>
                              <div className="space-y-2">
                                  <label className="type-label text-slate-500 ml-1">Home Airport</label>
                                  <input className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink uppercase" value={profileForm.home_airport || ''} onChange={e => setProfileForm({...profileForm, home_airport: e.target.value})} placeholder="e.g. AKL" maxLength={3} />
                              </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                  <label className="type-label text-slate-500 ml-1">Driver's License #</label>
                                  <input className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink uppercase" value={profileForm.license_number || ''} onChange={e => setProfileForm({...profileForm, license_number: e.target.value})} placeholder="For rental vans" />
                              </div>
                              <div className="space-y-2">
                                  <label className="type-label text-slate-500 ml-1">License Expiry</label>
                                  <input type="date" className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink [color-scheme:dark]" value={profileForm.license_expiry || ''} onChange={e => setProfileForm({...profileForm, license_expiry: e.target.value})} />
                              </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                  <label className="type-label text-slate-500 ml-1">T-Shirt Size</label>
                                  <select className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink appearance-none" value={profileForm.shirt_size || ''} onChange={e => setProfileForm({...profileForm, shirt_size: e.target.value})}>
                                      <option value="">Select...</option>
                                      <option value="XS">XS</option>
                                      <option value="S">Small</option>
                                      <option value="M">Medium</option>
                                      <option value="L">Large</option>
                                      <option value="XL">XL</option>
                                      <option value="XXL">XXL</option>
                                      <option value="XXXL">XXXL</option>
                                  </select>
                              </div>
                              <div className="space-y-2">
                                  <label className="type-label text-slate-500 ml-1">Diet / Allergies</label>
                                  <input className="w-full bg-slate-950 border border-white/10 rounded-xl p-4 text-white outline-none focus:border-brand-neonPink" value={profileForm.dietary_requirements || ''} onChange={e => setProfileForm({...profileForm, dietary_requirements: e.target.value})} placeholder="e.g. GF, Vegan" />
                              </div>
                          </div>

                          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl space-y-4 mt-4">
                              <h5 className="text-[10px] font-black text-red-400 uppercase tracking-widest flex items-center gap-1.5"><AlertCircle size={12}/> Emergency Contact</h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <input type="text" className="w-full bg-slate-950 border border-red-500/30 rounded-xl p-4 text-white text-sm outline-none focus:border-red-500 transition-colors" placeholder="Contact Name" value={profileForm.emergency_contact_name || ''} onChange={e => setProfileForm({...profileForm, emergency_contact_name: e.target.value})} />
                                  <input type="tel" className="w-full bg-slate-950 border border-red-500/30 rounded-xl p-4 text-white text-sm outline-none focus:border-red-500 transition-colors" placeholder="Contact Phone" value={profileForm.emergency_contact_phone || ''} onChange={e => setProfileForm({...profileForm, emergency_contact_phone: e.target.value})} />
                              </div>
                          </div>

                          <button 
                              onClick={async () => {
                                  try {
                                      const savedProfile = await saveCrewProfile(profileForm);
                                      setCurrentUser(savedProfile);
                                      alert("Profile successfully updated!");
                                  } catch (err: any) {
                                      alert("Failed to save: " + err.message);
                                  }
                              }}
                              className="w-full py-5 rounded-2xl bg-brand-neonPink text-white font-bold uppercase tracking-widest text-sm shadow-neon-pink mt-6 hover:scale-[1.02] transition-transform"
                          >
                              Save My Profile
                          </button>
                      </div>
                  </div>
              </div>
          ) : listTab === 'AVAILABILITY' ? (
              <div className="animate-fade-in-up">
                  <div className="bg-slate-900/60 border border-white/10 rounded-[32px] p-6 shadow-xl">
                      <div className="flex justify-between items-center mb-6">
                          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))} className="p-2 text-brand-neonBlue hover:bg-brand-neonBlue/10 rounded-xl transition-colors"><ChevronLeft size={24}/></button>
                          <h3 className="text-white font-bold text-lg tracking-widest uppercase">{monthName}</h3>
                          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))} className="p-2 text-brand-neonBlue hover:bg-brand-neonBlue/10 rounded-xl transition-colors"><ChevronLeft size={24} className="rotate-180"/></button>
                      </div>

                      {/* ⚡ ALIGNED MONDAY-FIRST CALENDAR HEADERS */}
                      <div className="grid grid-cols-7 gap-2 text-center mb-4">
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
                              <div key={d} className="type-label text-slate-500">{d}</div>
                          ))}
                      </div>

                      <div className="grid grid-cols-7 gap-2">
                          {/* ⚡ RIGID PADDING SO THE GRID ENGINE DOESN'T CRUSH EMPTY DAYS */}
                          {Array.from({ 
                              length: new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() === 0 
                              ? 6 
                              : new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() - 1 
                          }).map((_, i) => <div key={`empty-${i}`} className="aspect-square pointer-events-none" />)}
                          
                          {/* ACTUAL DAYS */}
                          {Array.from({ length: daysInMonth }).map((_, i) => {
                              const day = i + 1;
                              const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              
                              // ⚡ NEW: Identify if this specific box is TODAY
                              const todayDate = new Date();
                              const isToday = dateStr === `${todayDate.getFullYear()}-${String(todayDate.getMonth() + 1).padStart(2, '0')}-${String(todayDate.getDate()).padStart(2, '0')}`;
                              
                              const confirmedGig = myJobs.find(job => job.eventDate && job.eventDate.startsWith(dateStr)) || phaseJobs.filter(p=>p.status==='CONFIRMED'&&!p.cancelled&&nzLocal(p.startsAt).slice(0,10)<=dateStr&&nzLocal(new Date(Date.parse(p.endsAt)-1).toISOString()).slice(0,10)>=dateStr).map(p=>({...p,title:p.event+' · '+p.kind}))[0];
                              
                              // ⚡ FIX: Proper Timezone parsing so pink dots show on the exact local NZ day!
                              const pendingTasksCount = myTasks.filter(t => {
                                  if (!t.due_date || t.status === 'COMPLETED') return false;
                                  const localTaskDate = new Date(t.due_date);
                                  const taskDateStr = `${localTaskDate.getFullYear()}-${String(localTaskDate.getMonth() + 1).padStart(2, '0')}-${String(localTaskDate.getDate()).padStart(2, '0')}`;
                                  return taskDateStr === dateStr;
                              }).length;

                              const availStatus = availabilities[dateStr]?.status || 'AVAILABLE';
                              
                              let badgeClass = 'border-white/10 text-slate-400 bg-slate-950/50 hover:border-brand-neonBlue/50';
                              if (confirmedGig) {
                                  badgeClass = 'bg-brand-neonBlue text-slate-950 font-bold border-brand-neonBlue shadow-[0_0_15px_rgba(34,211,238,0.4)]';
                              } else if (availStatus === 'AVAILABLE') {
                                  badgeClass = 'bg-green-500/10 text-green-400 border-green-500/30';
                              } else if (availStatus === 'UNAVAILABLE') {
                                  badgeClass = 'bg-red-500/10 text-red-400 border-red-500/30 opacity-50';
                              } else if (availStatus === 'PARTIAL') {
                                  badgeClass = 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30';
                              }

                              // ⚡ UPGRADED: Add a striking white outline and bright text for TODAY
                              if (isToday) {
                                  badgeClass += ' outline outline-2 outline-white outline-offset-2 z-10 !text-white font-black shadow-[0_0_15px_rgba(255,255,255,0.3)]';
                              }

                              return (
                                  <button 
                                      key={day} 
                                      onClick={() => {
                                          if (confirmedGig) {
                                              alert(`You have a confirmed gig on this day: ${confirmedGig.title}\n\nPlease contact Admin if you need to cancel.`);
                                              return;
                                          }
                                          if (pendingTasksCount > 0) {
                                              alert(`You have ${pendingTasksCount} pending task(s) due today! Switch to the 'My Tasks' tab to view details.`);
                                          }
                                          setSelectedDate(dateStr);
                                          setAvailForm({
                                              status: availabilities[dateStr]?.status || 'AVAILABLE',
                                              startTime: availabilities[dateStr]?.start_time || '12:00',
                                              endTime: availabilities[dateStr]?.end_time || '23:00'
                                          });
                                      }}
                                      className={`aspect-square flex items-center justify-center rounded-xl border transition-all relative ${badgeClass}`}
                                  >
                                      {day}
                                      {/* ⚡ UPGRADED: RENDER A PINK DOT IF TASKS ARE DUE */}
                                      {pendingTasksCount > 0 && (
                                          <div className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-brand-neonPink rounded-full shadow-[0_0_10px_rgba(236,72,153,0.9)] animate-pulse border border-slate-950"></div>
                                      )}
                                  </button>
                              );
                          })}
                      </div>

                      {/* ⚡ CLEANED UP LEGEND */}
                      <div className="flex flex-wrap justify-center gap-4 mt-6 pt-4 border-t border-white/5">
                          <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-brand-neonBlue shadow-[0_0_8px_rgba(34,211,238,0.8)]"></div> Booked</span>
                          <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-brand-neonPink shadow-[0_0_10px_rgba(236,72,153,0.8)] animate-pulse border border-slate-950"></div> Task Due</span>
                          <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-green-400"></div> Free</span>
                          <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div> Partial</span>
                          <span className="flex items-center gap-1.5 type-label text-slate-400"><div className="w-2.5 h-2.5 rounded-full bg-red-400"></div> Busy</span>
                      </div>
                  </div>
              </div>
          ) : (
              <div className="animate-fade-in-up space-y-10">
                  <section>
                      <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                          <AlertCircle size={14} className="text-brand-neonBlue"/> Broadcasted Shifts ({availableShifts.length})
                      </h2>
                      <div className="space-y-4">
                          {availableShifts.length === 0 && (
                              <div className="bg-slate-900/40 border border-dashed border-white/10 rounded-[32px] p-8 text-center">
                                  <p className="text-sm text-slate-500 font-medium italic">No new broadcasts available.</p>
                              </div>
                          )}
                          {availableShifts.map(job => (
                              <GlassCard key={job.id} className="p-6 border-brand-neonBlue/30 shadow-[0_0_20px_rgba(34,211,238,0.1)] rounded-[32px]">
                                  <div className="flex justify-between items-start mb-4">
                                      <div>
                                          <h3 className="font-bold text-white text-xl tracking-tight">{job.title}</h3>
                                          <p className="type-label text-brand-neonBlue mt-1">{job.companyName}</p>
                                      </div>
                                      <span className="bg-brand-neonBlue/10 text-brand-neonBlue type-label px-3 py-1.5 rounded-full border border-brand-neonBlue/20">
                                          Action Req
                                      </span>
                                  </div>
                                  
                                  <div className="mb-5"><CrewEventCardDetails job={job} /></div>
                                  
                                  <div className="flex gap-3">
                                      <Button 
                                          onClick={(e) => handleDeclineShift(e, job)} 
                                          disabled={processingJobId !== null} 
                                          variant="outline"
                                          className="flex-1 py-4 rounded-xl border-rose-500/30 text-rose-500 hover:bg-rose-500/10 type-button text-xs"
                                      >
                                          Decline
                                      </Button>
                                      <Button 
                                          onClick={(e) => handleAcceptShift(e, job)} 
                                          disabled={processingJobId !== null} 
                                          className="flex-[2] bg-brand-neonBlue text-slate-950 font-bold py-4 rounded-xl shadow-neon tracking-widest text-[10px] uppercase"
                                      >
                                          {processingJobId === job.id ? 'Processing...' : 'Accept Shift'}
                                      </Button>
                                  </div>
                              </GlassCard>
                          ))}
                      </div>
                  </section>

                  <section>
                      <h2 className="text-xs font-black text-slate-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                          <Briefcase size={14} className="text-brand-neonPink"/> My Booked Jobs ({myJobs.length})
                      </h2>
                      <div className="space-y-4">
                          {myJobs.length === 0 && (
                              <div className="bg-slate-900/40 border border-dashed border-white/10 rounded-[32px] p-8 text-center">
                                  <p className="text-sm text-slate-500 font-medium italic">Your roster is currently clear.</p>
                              </div>
                          )}
                          {myJobs.map(job => {
                              const isConfirmed = job.myStatus === 'CONFIRMED';
                              
                              return (
                                  <div 
                                      key={job.id} 
                                      onClick={() => handleSelectJob(job)}
                                      className={`p-6 rounded-[32px] border cursor-pointer active:scale-95 transition-all ${
                                          isConfirmed ? 'bg-slate-800/80 border-white/10 shadow-xl' : 'bg-slate-900/40 border-white/5 opacity-80'
                                      }`}
                                  >
                                      <div className="flex justify-between items-start mb-4">
                                          <div>
                                              <h3 className="font-bold text-white text-xl tracking-tight">{job.title}</h3>
                                              <p className="type-label text-slate-400 mt-1">{job.clientName}</p>
                                          </div>
                                          <span className={`type-label px-3 py-1.5 rounded-full border ${
                                              isConfirmed ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                                          }`}>
                                              {job.myStatus}
                                          </span>
                                      </div>
                                      
                                      <CrewEventCardDetails job={job} />
                                  </div>
                              );
                          })}
                      </div>
                  </section>

                  {/* ⚡ NEW: SHIFT HISTORY TOGGLE */}
                  {pastJobs.length > 0 && (
                      <div className="pt-8 border-t border-white/5">
                          <button 
                              onClick={() => setShowPastJobs(!showPastJobs)} 
                              className="w-full py-4 rounded-2xl border border-white/10 text-slate-400 hover:text-white hover:bg-white/5 type-button text-xs transition-all flex items-center justify-center gap-2"
                          >
                              <History size={16} /> {showPastJobs ? 'Hide Shift History' : `View Past Shifts (${pastJobs.length})`}
                          </button>

                          {showPastJobs && (
                              <div className="mt-6 space-y-4 animate-fade-in">
                                  {pastJobs.map(job => (
                                      <div 
                                          key={job.id} 
                                          onClick={() => handleSelectJob(job)}
                                          className="p-6 rounded-[32px] border border-white/5 bg-slate-950/40 opacity-70 hover:opacity-100 cursor-pointer transition-all"
                                      >
                                          <div className="flex justify-between items-start mb-4">
                                              <div>
                                                  <h3 className="font-bold text-slate-300 text-lg tracking-tight">{job.title}</h3>
                                                  <p className="type-label text-slate-500 mt-1">{job.clientName}</p>
                                              </div>
                                              <span className="type-label px-3 py-1.5 rounded-full border bg-slate-800 text-slate-400 border-slate-700">
                                                  Completed
                                              </span>
                                          </div>
                                          
                                          <div className="flex items-center gap-6 text-sm text-slate-400 mt-4 border-t border-white/5 pt-4">
                                              <div className="flex items-center gap-2 font-medium">
                                                  <Calendar size={14} className="text-slate-500"/>
                                                  {job.eventDate ? new Date(job.eventDate).toLocaleDateString('en-NZ', {month:'short', day:'numeric', year:'numeric'}) : 'TBD'}
                                              </div>
                                          </div>
                                      </div>
                                  ))}
                              </div>
                          )}
                      </div>
                  )}

              </div>
          )}
        </div>

        {/* ⚡ THE TAP & POP BOTTOM SHEET MODAL */}
        {selectedDate && (
            <div className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelectedDate(null)}>
                <div className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-t-[32px] sm:rounded-[32px] p-6 animate-slide-up shadow-2xl" onClick={e => e.stopPropagation()}>
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-xl font-bold text-white tracking-tight">
                            {new Date(selectedDate!).toLocaleDateString('en-NZ', { weekday: 'long', month: 'long', day: 'numeric' })}
                        </h3>
                        <button onClick={() => setSelectedDate(null)} className="p-2 bg-white/5 rounded-full text-slate-400 hover:text-white"><X size={20}/></button>
                    </div>

                    <div className="space-y-3 mb-6">
                        <button 
                            onClick={() => setAvailForm({...availForm, status: 'AVAILABLE'})}
                            className={`w-full p-4 rounded-2xl font-bold transition-all border flex items-center justify-center gap-2 ${availForm.status === 'AVAILABLE' ? 'bg-green-500/20 text-green-400 border-green-500/40' : 'bg-slate-950 text-slate-400 border-white/5 hover:border-white/20'}`}
                        >
                            <CheckCircle size={18}/> Available All Day
                        </button>
                        <button 
                            onClick={() => setAvailForm({...availForm, status: 'PARTIAL'})}
                            className={`w-full p-4 rounded-2xl font-bold transition-all border flex items-center justify-center gap-2 ${availForm.status === 'PARTIAL' ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/40' : 'bg-slate-950 text-slate-400 border-white/5 hover:border-white/20'}`}
                        >
                            <Clock size={18}/> Specific Hours Only
                        </button>
                        <button 
                            onClick={() => setAvailForm({...availForm, status: 'UNAVAILABLE'})}
                            className={`w-full p-4 rounded-2xl font-bold transition-all border flex items-center justify-center gap-2 ${availForm.status === 'UNAVAILABLE' ? 'bg-red-500/20 text-red-400 border-red-500/40' : 'bg-slate-950 text-slate-400 border-white/5 hover:border-white/20'}`}
                        >
                            <AlertCircle size={18}/> Unavailable (Block Out)
                        </button>
                    </div>

                    {availForm.status === 'PARTIAL' && (
                      <div className="flex gap-4 mb-6 bg-black/30 p-4 rounded-2xl border border-white/5 animate-fade-in">
                          <div className="flex-1">
                              <label className="type-label mb-1 block text-slate-500">Free From</label>
                              <select 
                                  value={availForm.startTime.slice(0, 5)} 
                                  onChange={e => setAvailForm({...availForm, startTime: e.target.value})} 
                                  className="w-full bg-slate-950 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-yellow-500 cursor-pointer appearance-none"
                              >
                                  {Array.from({ length: 24 * 4 }).map((_, i) => {
                                      const h = Math.floor(i / 4);
                                      const m = (i % 4) * 15;
                                      const ampm = h >= 12 ? 'PM' : 'AM';
                                      const hour12 = h % 12 || 12;
                                      const val = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                      const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                      return <option key={`start-${val}`} value={val}>{label}</option>;
                                  })}
                              </select>
                          </div>
                          <div className="flex-1">
                              <label className="type-label mb-1 block text-slate-500">Free Until</label>
                              <select 
                                  value={availForm.endTime.slice(0, 5)} 
                                  onChange={e => setAvailForm({...availForm, endTime: e.target.value})} 
                                  className="w-full bg-slate-950 border border-white/10 rounded-xl p-3 text-white outline-none focus:border-yellow-500 cursor-pointer appearance-none"
                              >
                                  {Array.from({ length: 24 * 4 }).map((_, i) => {
                                      const h = Math.floor(i / 4);
                                      const m = (i % 4) * 15;
                                      const ampm = h >= 12 ? 'PM' : 'AM';
                                      const hour12 = h % 12 || 12;
                                      const val = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                      const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm}`;
                                      return <option key={`end-${val}`} value={val}>{label}</option>;
                                  })}
                              </select>
                          </div>
                      </div>
                  )}

                    <Button onClick={handleSaveAvailability} className="w-full py-5 rounded-2xl bg-brand-neonBlue text-slate-950 font-bold shadow-neon-blue-sm">
                        Save Schedule
                    </Button>
                </div>
            </div>
        )}
      </div>
    );
  }

  // --- JOB DETAIL VIEW ---
  const isConfirmed = selectedJob.myStatus === 'CONFIRMED';
  const selectedCrewRecord = selectedJob.staff_roster?.crew?.find((crew: any) => crew.id === currentUser.id) || {};
  const selectedCrewRole = crewRole(selectedCrewRecord.role, currentUser?.role);
  const handlesEquipment = ['Senior Event Technician', 'Event Technician'].includes(selectedCrewRole);
  const missionSteps = getCrewChecklist(selectedCrewRole, { ...selectedJob,
      equipmentRevision: pickup?.revision ?? selectedJob.equipmentRevision,
      equipmentRequired: pickup ? pickup.items.length > 0 : selectedJob.equipmentRequired });
  const missionProgress = selectedCrewRecord.mission_progress || [];

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col">
      <div className="bg-slate-950/80 backdrop-blur-2xl border-b border-white/10 p-4 sticky top-0 z-20 shadow-2xl">
        <div className="flex items-center gap-4 mb-4">
          <button 
            onClick={() => setView('LIST')} 
            className="w-12 h-12 flex items-center justify-center bg-slate-900 rounded-2xl text-white hover:bg-slate-800 shrink-0 border border-white/5"
          >
            <ChevronLeft size={24} />
          </button>
          
          <div className="flex-1 min-w-0">
             <h2 className="text-white font-bold text-xl tracking-tight truncate px-1">{selectedJob.title}</h2>
             <p className="type-label text-brand-neonPink truncate px-1 mt-1">{selectedJob.venue}</p>
          </div>
        </div>

        {isConfirmed && (
            <div className="crew-shift-navigation flex bg-slate-900 p-1.5 rounded-2xl border border-white/5 overflow-x-auto custom-scrollbar gap-1">
            {[
                { id: 'OVERVIEW', label: 'Run Sheet' },
                { id: 'TRAVEL', label: 'Briefing' },
                { id: 'TASKS', label: 'Equipment' },
                { id: 'GEAR', label: 'Media' },
                { id: 'CREW', label: 'Team' }
            ].map((tab) => (
                <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 py-2.5 type-button text-xs rounded-xl transition-all ${
                    activeTab === tab.id ? 'bg-white text-slate-950 shadow-md' : 'text-slate-500 hover:text-white'
                }`}
                >
                {tab.label}
                </button>
            ))}
            </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-5 sm:p-6 pb-32 custom-scrollbar">
        
        {!isConfirmed ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8 mt-20">
                <div className="w-24 h-24 bg-yellow-500/10 border border-yellow-500/20 rounded-full flex items-center justify-center mb-6">
                    <Clock size={40} className="text-yellow-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 tracking-tight">Application Pending</h3>
                <p className="text-slate-400 font-medium leading-relaxed max-w-sm">
                    You have applied for this shift. Once Admin confirms you for the roster, the Event Blueprint, contact details, and full run sheet will unlock here.
                </p>
                <Button variant="outline" className="mt-8 px-8 py-4 rounded-xl" onClick={() => setView('LIST')}>Return to Roster</Button>
            </div>
        ) : (
            <div className="space-y-6 max-w-2xl mx-auto animate-fade-in-up">
                {activeTab === 'OVERVIEW' && (
                    <>
                        <CrewRunSheetPriority
                            job={selectedJob}
                            missionContent={(
                                <MissionControlCarousel
                                    steps={missionSteps}
                                    completedStepIds={missionProgress}
                                    busy={isMissionUploading}
                                    gearPickupBlocked={!equipmentControls.pickup || !pickup || (pickup.confirmed && pickup.outstanding > 0)}
                                    onComplete={handleCompleteMissionStep}
                                    onPhotoSelected={async (event) => {
                                        const file = event.target.files?.[0];
                                        if (!file) return;
                                        setIsMissionUploading(true);
                                        try {
                                            const reference = await uploadCrewMedia(file, currentUser.id, selectedJob.id, true);
                                            const saved = await crewOperation('crew_append_media', { p_shift: selectedJob.id, p_urls: [reference] });
                                            setSelectedJob((previous: any) => ({ ...previous, media_urls: saved.media_urls }));
                                            await handleCompleteMissionStep('SETUP_COMPLETE');
                                        } catch (error: any) {
                                            alert('Upload failed: ' + error.message);
                                        } finally {
                                            setIsMissionUploading(false);
                                        }
                                    }}
                                />
                            )}
                        />
                    </>
                )}

{/* ⚡ NEW: TRAVEL & BRIEFING TAB */}
{activeTab === 'TRAVEL' && (
                    <CrewEventBriefing
                        job={selectedJob}
                        blueprint={jobBlueprint}
                        loading={isFetchingBlueprint}
                        onDocumentOpen={handleForceDownload}
                    />
                )}

{activeTab === 'TASKS' && (<>
                    <CrewEquipmentDocuments shiftId={selectedJob.id}/>
                        <CrewEquipmentWorkspace
                        handlesEquipment={handlesEquipment}
                        pickupContent={handlesEquipment ? (
                            <CrewPickupPanel
                                key={selectedJob.id}
                                shiftId={selectedJob.id}
                                missionKey={JSON.stringify(selectedCrewRecord.mission_progress || [])}
                                onState={setPickup}
                            />
                        ) : null}
                        crewNotes={jobBlueprint?.data?.crewNotes || ''}
                        onCrewNotesChange={value => setJobBlueprint((previous: any) => ({
                            ...(previous || {}),
                            data: { ...(previous?.data || {}), crewNotes: value },
                        }))}
                        onCrewNotesSave={async value => {
                            if (jobBlueprint?.quoteId) {
                                await supabase.rpc('crew_quote_note', { p_quote_id: jobBlueprint.quoteId, p_note: value });
                            }
                        }}
                    />
                </>)}

                {activeTab === 'GEAR' && (
                    <div className="space-y-6 animate-fade-in">
                        <h3 className="text-[11px] font-black text-brand-neonBlue uppercase tracking-[0.2em] mb-4 flex items-center gap-2 px-2 border-b border-brand-neonBlue/20 pb-3">
                            <ImageIcon size={14} /> Media Upload Vault
                        </h3>
                        <p className="text-sm text-slate-400 mb-6 px-2">Upload event photos, test prints, or setup shots for the admin team. These save directly to the Admin Dashboard.</p>
                        
                        <div className="relative">
                            <input
                                type="file"
                                id="crew-media-upload"
                                className="hidden"
                                multiple
                                accept="image/*,video/*"
                                onChange={async (e) => {
                                    const files = e.target.files;
                                    if (!files || files.length === 0) return;
                                    
                                    setIsUploadingMedia(true);
                                    try {
                                        const uploadedUrls = [];
                                        for (let i = 0; i < files.length; i++) {
                                            const file = files[i];
                                            uploadedUrls.push(await uploadCrewMedia(file, currentUser.id, selectedJob.id));
                                        }
                                        
                                        // ⚡ NEW LOGIC: Save to event_shifts table, not quotes table
                                        const saved = await crewOperation('crew_append_media', { p_shift: selectedJob.id, p_urls: uploadedUrls });
                                        const newMedia = saved.media_urls;

                                        // Update local UI
                                        setSelectedJob({ ...selectedJob, media_urls: newMedia });
                                        alert("Photos attached to this shift successfully!");

                                    } catch (err: any) {
                                        alert("Failed to upload media: " + err.message);
                                    } finally {
                                        setIsUploadingMedia(false);
                                    }
                                }}
                            />
                            <label htmlFor="crew-media-upload" className="flex flex-col items-center justify-center w-full p-10 border-2 border-dashed border-brand-neonBlue/30 rounded-3xl cursor-pointer bg-brand-neonBlue/5 hover:bg-brand-neonBlue/10 transition-all shadow-inner">
                                {isUploadingMedia ? (
                                    <div className="flex flex-col items-center">
                                        <div className="w-10 h-10 border-4 border-brand-neonBlue border-t-transparent rounded-full animate-spin mb-4"></div>
                                        <span className="text-brand-neonBlue type-button text-xs">Uploading to Vault...</span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center">
                                        <div className="w-16 h-16 bg-brand-neonBlue/10 rounded-full flex items-center justify-center mb-4 border border-brand-neonBlue/20 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
                                            <Upload size={28} className="text-brand-neonBlue" />
                                        </div>
                                        <span className="text-white font-bold text-lg tracking-tight">Tap to Upload Media</span>
                                        <span className="text-xs text-slate-500 mt-2 font-medium">Supports multiple photos/videos at once</span>
                                    </div>
                                )}
                            </label>
                        </div>

                        {/* ⚡ UPDATED: Pulls from the Shift Media bucket */}
                        {(selectedJob.media_urls || []).length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
                                {selectedJob.media_urls.map((url: string, idx: number) => {
                                    const isVideo = url.match(/\.(mp4|webm|mov)$/i);
                                    return (
                                        <div key={idx} className="aspect-square rounded-2xl border border-white/10 overflow-hidden bg-black group relative shadow-lg">
                                            {isVideo ? (
                                                <PrivateVideo src={url} className="w-full h-full object-cover opacity-80" muted playsInline />
                                            ) : (
                                                <PrivateImage src={url} alt="Crew Upload" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity group-hover:scale-105 duration-500" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                    </div>
                )}
                
                {activeTab === 'CREW' && (
                    <div className="space-y-4 animate-fade-in">
                        <h3 className="text-[11px] font-black text-brand-neonBlue uppercase tracking-[0.2em] mb-4 flex items-center gap-2 px-2 border-b border-brand-neonBlue/20 pb-3">
                            <Users size={14} /> On-Site Team Roster
                        </h3>
                        <div className="space-y-3">
                            {/* ⚡ ONLY SHOW CREW MEMBERS WHO ARE OFFICIALLY CONFIRMED! */}
                            {selectedJob.staff_roster?.crew?.filter((c: any) => c.status === 'CONFIRMED').map((member: any) => (
                                <GlassCard key={member.id} className="p-4 rounded-2xl border-white/5 flex justify-between items-center bg-slate-900/50">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-white font-bold border border-white/10">
                                            {member.name.substring(0,2).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="text-white font-bold text-sm">
                                                {member.name} {member.id === currentUser?.id && <span className="text-brand-neonPink text-[10px] uppercase ml-1">(You)</span>}
                                            </p>
                                            <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">
                                                {normalizeStaffRole(member.role)} • Call: <span className="text-white font-bold">{member.call_time ? formatTime(member.call_time) : 'Not confirmed'}</span>
                                            </p>
                                        </div>
                                    </div>
                                    
                                    {member.id !== currentUser?.id && member.phone && (
                                        <div className="flex gap-2">
                                            <a href={`tel:${member.phone}`} className="w-10 h-10 flex items-center justify-center bg-slate-800 text-green-400 rounded-xl hover:bg-slate-700 transition-colors border border-white/5">
                                                <Phone size={16}/>
                                            </a>
                                            <a href={`sms:${member.phone}`} className="w-10 h-10 flex items-center justify-center bg-slate-800 text-brand-neonBlue rounded-xl hover:bg-slate-700 transition-colors border border-white/5">
                                                <MessageSquare size={16}/>
                                            </a>
                                        </div>
                                    )}
                                </GlassCard>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        )}
      </div>
    </div>
  );
};
